import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} [sliderMin] - The minimum price slider
 * @property {HTMLInputElement} [sliderMax] - The maximum price slider
 * @property {HTMLElement} [sliderTrack] - The slider active track element
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initSlider();
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Initializes slider min/max/value from data attributes, parsing locale-formatted values
   */
  #initSlider() {
    const { sliderMin, sliderMax } = this.refs;
    const container = /** @type {HTMLElement | null} */ (this.querySelector('.price-facet__slider'));
    if (!sliderMin || !sliderMax || !container) return;

    const max = Number(formatMoney(container.dataset.max ?? '0'));
    const minVal = Number(formatMoney(container.dataset.minValue ?? '0'));
    const maxVal = Number(formatMoney(container.dataset.maxValue ?? String(max)));

    sliderMin.max = sliderMax.max = String(max);
    sliderMin.value = String(minVal);
    sliderMax.value = String(maxVal);
    this.#updateSliderTrack();
  }

  /**
   * Formats a numeric value for display in text inputs, matching the locale format
   * used by money_without_currency (detected from the max input placeholder).
   * @param {number} value - The numeric value to format
   * @returns {string} The locale-formatted string
   */
  #formatForInput(value) {
    const placeholder = this.refs.maxInput.placeholder;
    const commaMatch = placeholder.match(/,(\d+)$/);
    if (commaMatch) {
      return value.toFixed(commaMatch[1].length).replace('.', ',');
    }
    const dotMatch = placeholder.match(/\.(\d+)$/);
    if (dotMatch) {
      return value.toFixed(dotMatch[1].length);
    }
    return String(Math.round(value));
  }

  /**
   * Handles slider input events for real-time sync
   * @param {Event} event - The input event from the range slider
   */
  handleSliderInput(event) {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const minVal = parseFloat(sliderMin.value);
    const maxVal = parseFloat(sliderMax.value);

    // Prevent handles from crossing
    if (minVal > maxVal) {
      if (event.target === sliderMin) {
        sliderMin.value = String(maxVal);
      } else {
        sliderMax.value = String(minVal);
      }
    }

    // Sync to text inputs with locale-aware formatting
    const currentMin = parseFloat(sliderMin.value);
    const currentMax = parseFloat(sliderMax.value);
    minInput.value = currentMin > 0 ? this.#formatForInput(currentMin) : '';
    maxInput.value = currentMax < parseFloat(sliderMax.max) ? this.#formatForInput(currentMax) : '';

    this.#updateSliderTrack();
  }

  /**
   * Updates the colored portion of the slider track
   */
  #updateSliderTrack() {
    const { sliderMin, sliderMax, sliderTrack } = this.refs;
    if (!sliderMin || !sliderMax || !sliderTrack) return;

    const max = parseFloat(sliderMax.max) || 1;
    const minPercent = (parseFloat(sliderMin.value) / max) * 100;
    const maxPercent = (parseFloat(sliderMax.value) / max) * 100;

    sliderTrack.style.left = `${minPercent}%`;
    sliderTrack.style.right = `${100 - maxPercent}%`;
  }

  /**
   * Syncs slider positions from text input values, parsing locale-formatted numbers
   */
  #syncSlidersFromInputs() {
    const { minInput, maxInput, sliderMin, sliderMax } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const minVal = minInput.value ? Number(formatMoney(minInput.value)) : 0;
    const maxVal = maxInput.value ? Number(formatMoney(maxInput.value)) : Number(sliderMax.max);
    sliderMin.value = String(isNaN(minVal) ? 0 : minVal);
    sliderMax.value = String(isNaN(maxVal) ? sliderMax.max : maxVal);
    this.#updateSliderTrack();
  }

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#syncSlidersFromInputs();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(formatMoney(input.value));
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = this.#formatForInput(min);
    if (value > max) input.value = this.#formatForInput(max);
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * @typedef {Object} LengthFacetRefs
 * @property {HTMLInputElement} minInput - The minimum length input
 * @property {HTMLInputElement} maxInput - The maximum length input
 * @property {HTMLInputElement} [sliderMin] - The minimum length slider
 * @property {HTMLInputElement} [sliderMax] - The maximum length slider
 * @property {HTMLElement} [sliderTrack] - The slider active track element
 */

/**
 * Handles length facet functionality with range slider and hidden checkboxes.
 * Adapts the range slider UX for list-type filters by syncing slider positions
 * with hidden checkbox inputs.
 * @extends {Component<LengthFacetRefs>}
 */
class LengthFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#initSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initSlider();
  }

  /**
   * Restricts input to numeric characters only
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;
    const pattern = /[0-9]|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * sessionStorage key for persisting free-form slider positions
   */
  get #storageKey() {
    return `length-filter:${this.id}`;
  }

  /**
   * Initializes slider min/max/value from data attributes,
   * restoring free-form positions from sessionStorage when a filter is active.
   */
  #initSlider() {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    const container = /** @type {HTMLElement | null} */ (this.querySelector('.price-facet__slider'));
    if (!sliderMin || !sliderMax || !container) return;

    const min = Number(container.dataset.min ?? '0');
    const max = Number(container.dataset.max ?? '0');
    let minVal = Number(container.dataset.minValue ?? String(min));
    let maxVal = Number(container.dataset.maxValue ?? String(max));

    // Restore free-form slider positions when checkboxes are active
    const hasActiveValues = this.querySelector('input[type="checkbox"]:checked') !== null;
    if (hasActiveValues) {
      try {
        const stored = JSON.parse(sessionStorage.getItem(this.#storageKey));
        if (stored) {
          if (stored.min >= min && stored.min <= max) minVal = stored.min;
          if (stored.max >= min && stored.max <= max) maxVal = stored.max;
        }
      } catch {}
    } else {
      sessionStorage.removeItem(this.#storageKey);
    }

    sliderMin.min = sliderMax.min = String(min);
    sliderMin.max = sliderMax.max = String(max);
    sliderMin.value = String(minVal);
    sliderMax.value = String(maxVal);
    minInput.value = minVal > min ? String(minVal) : '';
    maxInput.value = maxVal < max ? String(maxVal) : '';
    this.#updateSliderTrack();
  }

  /**
   * Handles slider input events for real-time sync
   * @param {Event} event
   */
  handleSliderInput(event) {
    const { sliderMin, sliderMax, minInput, maxInput } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const minVal = parseFloat(sliderMin.value);
    const maxVal = parseFloat(sliderMax.value);

    // Prevent handles from crossing
    if (minVal > maxVal) {
      if (event.target === sliderMin) {
        sliderMin.value = String(maxVal);
      } else {
        sliderMax.value = String(minVal);
      }
    }

    const currentMin = parseFloat(sliderMin.value);
    const currentMax = parseFloat(sliderMax.value);
    const rangeMin = parseFloat(sliderMin.min);
    const rangeMax = parseFloat(sliderMax.max);

    minInput.value = currentMin > rangeMin ? String(currentMin) : '';
    maxInput.value = currentMax < rangeMax ? String(currentMax) : '';

    this.#updateSliderTrack();
  }

  /**
   * Updates the colored portion of the slider track, accounting for non-zero minimum
   */
  #updateSliderTrack() {
    const { sliderMin, sliderMax, sliderTrack } = this.refs;
    if (!sliderMin || !sliderMax || !sliderTrack) return;

    const min = parseFloat(sliderMin.min);
    const max = parseFloat(sliderMax.max);
    const range = max - min || 1;
    const minPercent = ((parseFloat(sliderMin.value) - min) / range) * 100;
    const maxPercent = ((parseFloat(sliderMax.value) - min) / range) * 100;

    sliderTrack.style.left = `${minPercent}%`;
    sliderTrack.style.right = `${100 - maxPercent}%`;
  }

  /**
   * Main handler: validates inputs, syncs sliders and checkboxes, submits form
   */
  updateLengthFilterAndResults() {
    const { minInput, maxInput, sliderMin, sliderMax } = this.refs;
    if (!sliderMin || !sliderMax) return;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    // Sync sliders from text inputs
    const rangeMin = parseFloat(sliderMin.min);
    const rangeMax = parseFloat(sliderMax.max);
    const minVal = minInput.value ? Number(minInput.value) : rangeMin;
    const maxVal = maxInput.value ? Number(maxInput.value) : rangeMax;
    sliderMin.value = String(isNaN(minVal) ? rangeMin : minVal);
    sliderMax.value = String(isNaN(maxVal) ? rangeMax : maxVal);
    this.#updateSliderTrack();

    // Persist free-form slider positions (or clear if full range)
    const isFullRange = parseFloat(sliderMin.value) <= rangeMin && parseFloat(sliderMax.value) >= rangeMax;
    if (isFullRange) {
      sessionStorage.removeItem(this.#storageKey);
    } else {
      sessionStorage.setItem(this.#storageKey, JSON.stringify({
        min: parseFloat(sliderMin.value),
        max: parseFloat(sliderMax.value)
      }));
    }

    // Sync hidden checkboxes to match the selected range
    this.#syncCheckboxes(parseFloat(sliderMin.value), parseFloat(sliderMax.value));

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Clamps input value to its data-min / data-max range
   * @param {HTMLInputElement} input
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min') ?? '');
    const max = Number(input.getAttribute('data-max') ?? '');
    if (value < min) input.value = String(min);
    if (value > max) input.value = String(max);
  }

  /**
   * Cross-constrains the min/max text inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', maxInput.getAttribute('data-min') ?? '');
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max') ?? '');
  }

  /**
   * Checks/unchecks hidden checkboxes based on whether their numeric value
   * falls within the selected range. Full range = all unchecked (no filter).
   * @param {number} currentMin
   * @param {number} currentMax
   */
  #syncCheckboxes(currentMin, currentMax) {
    const { sliderMin, sliderMax } = this.refs;
    if (!sliderMin || !sliderMax) return;

    const rangeMin = parseFloat(sliderMin.min);
    const rangeMax = parseFloat(sliderMax.max);
    const isFullRange = currentMin <= rangeMin && currentMax >= rangeMax;

    this.querySelectorAll('input[type="checkbox"][data-numeric-value]').forEach((cb) => {
      if (!(cb instanceof HTMLInputElement)) return;
      if (isFullRange) {
        cb.checked = false;
      } else {
        const val = Number(cb.dataset.numericValue);
        cb.checked = val >= currentMin && val <= currentMax;
      }
    });
  }

  /**
   * Updates the facet status summary with the selected range
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    const container = this.querySelector('.price-facet__slider');
    const rangeMin = container?.dataset.min || '0';
    const rangeMax = container?.dataset.max || '0';
    const unit = this.dataset.unit || '';

    statusComponent.updateLengthSummary(minInput, maxInput, rangeMin, rangeMax, unit);
  }
}

if (!customElements.get('length-facet-component')) {
  customElements.define('length-facet-component', LengthFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component, length-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    // Handle clearing an entire length filter via its target component
    const clearTarget = this.dataset.clearTarget;
    if (clearTarget) {
      // Clear sessionStorage for the length filter
      const targetEl = document.getElementById(clearTarget);
      if (targetEl instanceof LengthFacetComponent) {
        sessionStorage.removeItem(`length-filter:${clearTarget}`);
      }

      // Find all matching components across all forms (desktop + drawer)
      document.querySelectorAll(`#${CSS.escape(clearTarget)}`).forEach((target) => {
        target.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
          if (cb instanceof HTMLInputElement) cb.checked = false;
        });
        target.querySelectorAll('input[type="text"]').forEach((input) => {
          if (input instanceof HTMLInputElement) input.value = '';
        });
      });

      // Find the closest facetsForm or the one specified by form param
      const facetsForm = form
        ? document.getElementById(form)
        : document.querySelector('facets-form-component');

      if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFilters();
      return;
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Updates the length summary with a plain numeric range
   * @param {HTMLInputElement} minInput - The minimum length input
   * @param {HTMLInputElement} maxInput - The maximum length input
   * @param {string} rangeMin - The minimum range value
   * @param {string} rangeMax - The maximum range value
   * @param {string} unit - The unit label (e.g. 'mm')
   */
  updateLengthSummary(minInput, maxInput, rangeMin, rangeMax, unit) {
    const { facetStatus } = this.refs;

    if (!minInput.value && !maxInput.value) {
      facetStatus.textContent = '';
      return;
    }

    const minDisplay = minInput.value || rangeMin;
    const maxDisplay = maxInput.value || rangeMax;
    facetStatus.textContent = `${minDisplay}\u2013${maxDisplay} ${unit}`.trim();
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.textContent = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
